// /app/chapter-test — start a Chapter Test (BRIEFS C7.4, SPEC §5 item 5).
// Pick a chapter → status card (Total, Attempted, Correct, Incorrect, Unattempted) + "Reset
// Chapter" (asks first) → three checkboxes (Attempted correct / Attempted incorrect /
// Unattempted) → Start. The kinds use the chapter's ROUND status (since the last reset).
// "PYQs only" switch (C11.4): the kinds count only the chapter's PYQs (status.pyq).
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  getChapterStatus, getOpenTest, getTestChapters, resetChapter, startChapterTest,
} from '../../api/v2';
import { track, useActivityArea } from '../../components/app/activity';
import { ConfirmDialog, OpenTestBanner, errorText } from '../../components/app/testParts';
import { C, FONT, button } from '../../components/app/theme';
import { CheckRow, PyqSwitch, chapterName } from './AppPractice';
import { Empty, LoadError, Loading } from '../../components/app/LoadState';

const MAX_DEFAULT = 90; // the server gives at most 90 questions when no count is sent

const box = {
  boxSizing: 'border-box', padding: 20, background: C.surface, border: `1px solid ${C.border}`,
  borderRadius: 12, display: 'flex', flexDirection: 'column', gap: 12,
};
const h2 = { fontFamily: FONT.heading, margin: 0, fontSize: 17, fontWeight: 600 };

const KINDS = [
  { key: 'correct', label: 'Attempted correct', field: 'correct' },
  { key: 'incorrect', label: 'Attempted incorrect', field: 'incorrect' },
  { key: 'unattempted', label: 'Unattempted', field: 'unattempted' },
];

function Figure({ label, value, color, testId }) {
  return (
    <div data-testid={testId} style={{
      flex: '1 1 110px', boxSizing: 'border-box', padding: '12px 14px', borderRadius: 10,
      background: C.bg, border: `1px solid ${C.border}`, display: 'flex', flexDirection: 'column', gap: 2,
    }}>
      <div style={{ fontSize: 13, color: C.muted }}>{label}</div>
      <div style={{ fontFamily: FONT.heading, fontSize: 24, fontWeight: 700, color: color || C.text }}>{value}</div>
    </div>
  );
}

export default function AppChapterTest() {
  useActivityArea('test');
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [params, setParams] = useSearchParams();
  const chapterId = Number(params.get('chapter')) || null;
  const [kinds, setKinds] = useState(['unattempted']);
  const [asking, setAsking] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [pyqOnly, setPyqOnly] = useState(false);

  const chaptersQ = useQuery({ queryKey: ['v2', 'test-chapters'], queryFn: getTestChapters });
  const openQ = useQuery({ queryKey: ['v2', 'open-test'], queryFn: getOpenTest });
  const statusQ = useQuery({
    queryKey: ['v2', 'chapter-status', chapterId],
    queryFn: () => getChapterStatus(chapterId),
    enabled: !!chapterId,
  });
  const chapters = useMemo(() => (chaptersQ.data && chaptersQ.data.chapters) || [], [chaptersQ.data]);
  const chapter = chapters.find((c) => c.chapter_id === chapterId);
  const status = statusQ.data;
  const openTest = openQ.data && openQ.data.test;

  // Only one chapter open → choose it at once.
  useEffect(() => {
    if (!chapterId && chapters.length === 1) setParams({ chapter: String(chapters[0].chapter_id) }, { replace: true });
  }, [chapterId, chapters, setParams]);

  // Fresh choice of kinds for every chapter: the unattempted ones, or the wrong ones when all are attempted.
  useEffect(() => {
    if (!status) return;
    setKinds(status.unattempted > 0 ? ['unattempted'] : status.incorrect > 0 ? ['incorrect'] : ['correct']);
  }, [status && status.chapter_id]);

  const counts = status ? (pyqOnly ? status.pyq || {} : status) : null;
  const matching = counts ? KINDS.filter((k) => kinds.includes(k.key)).reduce((n, k) => n + (counts[k.field] || 0), 0) : 0;
  const pyqTotal = status && status.pyq ? status.pyq.total : 0;
  const testSize = Math.min(matching, MAX_DEFAULT);
  const canStart = !!status && matching > 0 && !busy;

  const toggle = (k) => setKinds((ks) => (ks.includes(k) ? ks.filter((x) => x !== k) : [...ks, k]));

  async function doReset() {
    setBusy(true);
    setError('');
    try {
      await resetChapter(chapterId);
      await qc.invalidateQueries({ queryKey: ['v2', 'chapter-status', chapterId] });
      setAsking(false);
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  }

  async function start() {
    if (!canStart) return;
    setBusy(true);
    setError('');
    const chosen = KINDS.map((k) => k.key).filter((k) => kinds.includes(k));
    try {
      const res = await startChapterTest(chapterId, chosen, pyqOnly);
      track('test_start', { test_id: res.test_id, chapter_id: chapterId, details: { mode: 'v2_chapter', kinds: chosen, pyq_only: pyqOnly } });
      navigate(`/app/test/${res.test_id}`);
    } catch (err) {
      setError(errorText(err));
      setBusy(false);
    }
  }

  return (
    <div data-testid="v2-chapter-test" style={{ maxWidth: 820, display: 'flex', flexDirection: 'column', gap: 20 }}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        <h1 style={{ fontFamily: FONT.heading, margin: 0, fontSize: 28, fontWeight: 600 }}>Chapter Test</h1>
        <div style={{ fontSize: 15, color: C.muted }}>One chapter. You see right or wrong after every answer.</div>
      </div>

      <OpenTestBanner test={openTest} note="If you start a new test, this test is finished first." />

      <div style={box}>
        <h2 style={h2}>1. Choose a chapter</h2>
        {chaptersQ.isLoading && <Loading text="Loading chapters…" />}
        {chaptersQ.isError && <LoadError onRetry={chaptersQ.refetch} />}
        {!chaptersQ.isLoading && !chaptersQ.isError && chapters.length === 0 && (
          <Empty text="No chapters are open yet." />
        )}
        <div role="radiogroup" style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {chapters.map((ch) => {
            const on = ch.chapter_id === chapterId;
            return (
              <button key={ch.chapter_id} type="button" role="radio" aria-checked={on}
                data-testid={`v2-ch-${ch.chapter_id}`} onClick={() => setParams({ chapter: String(ch.chapter_id) })}
                style={{
                  display: 'flex', alignItems: 'center', gap: 12, minHeight: 48, padding: '8px 14px',
                  boxSizing: 'border-box', width: '100%', borderRadius: 10, textAlign: 'left', cursor: 'pointer',
                  border: `2px solid ${on ? C.primary : C.border}`, background: on ? C.tint : C.surface,
                  fontFamily: FONT.ui, fontSize: 15, color: C.text, fontWeight: on ? 600 : 400,
                }}>
                <span aria-hidden="true" style={{
                  width: 20, height: 20, borderRadius: 10, flexShrink: 0, boxSizing: 'border-box',
                  border: `2px solid ${on ? C.primary : '#94A3B8'}`, background: C.surface,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>{on && <span style={{ width: 10, height: 10, borderRadius: 5, background: C.primary }} />}</span>
                {chapterName(ch)}
              </button>
            );
          })}
        </div>
      </div>

      {chapterId && (
        <div style={box} data-testid="v2-chapter-status">
          <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
            <h2 style={h2}>{chapter ? chapter.title : 'This chapter'}</h2>
            <button type="button" data-testid="v2-reset-chapter" onClick={() => setAsking(true)} disabled={!status}
              style={{ ...button.secondary, minHeight: 44, fontSize: 14 }}>Reset Chapter</button>
          </div>
          {statusQ.isLoading && <Loading />}
          {statusQ.isError && <LoadError onRetry={statusQ.refetch} />}
          {status && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
              <Figure testId="v2-st-total" label="Total" value={status.total} />
              <Figure testId="v2-st-attempted" label="Attempted" value={status.attempted} />
              <Figure testId="v2-st-correct" label="Correct" value={status.correct} color={C.good} />
              <Figure testId="v2-st-incorrect" label="Incorrect" value={status.incorrect} color={C.bad} />
              <Figure testId="v2-st-unattempted" label="Unattempted" value={status.unattempted} />
            </div>
          )}
        </div>
      )}

      {status && (
        <div style={box}>
          <h2 style={h2}>2. Which questions?</h2>
          <PyqSwitch on={pyqOnly} onClick={() => setPyqOnly((v) => !v)} count={pyqTotal} />
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {KINDS.map((k) => (
              <CheckRow key={k.key} checked={kinds.includes(k.key)} onClick={() => toggle(k.key)} testId={`v2-kind-${k.key}`}>
                <span style={{ flexGrow: 1 }}>{k.label}{pyqOnly ? ' · PYQs' : ''}</span>
                <span data-testid={`v2-kind-count-${k.key}`} style={{ fontSize: 13, color: C.muted, whiteSpace: 'nowrap' }}>{counts[k.field] || 0}</span>
              </CheckRow>
            ))}
          </div>
          <div data-testid="v2-chapter-summary" style={{ fontSize: 14, color: C.muted }}>
            {kinds.length === 0 ? 'Choose at least one box.'
              : matching === 0 ? (pyqOnly ? 'No PYQs of this kind. Choose another box.' : 'No questions of this kind. Choose another box.')
                : matching > MAX_DEFAULT ? `Your test will have ${MAX_DEFAULT} questions (the most in one test).`
                  : `Your test will have ${testSize} ${pyqOnly ? (testSize === 1 ? 'PYQ' : 'PYQs') : (testSize === 1 ? 'question' : 'questions')}.`}
          </div>
        </div>
      )}

      {error && <div data-testid="v2-start-error" style={{ color: C.bad, fontSize: 15 }}>{error}</div>}
      {status && (
        <div>
          <button type="button" data-testid="v2-start" onClick={start} disabled={!canStart}
            style={{ ...button.primary, minHeight: 50, padding: '0 32px', opacity: canStart ? 1 : 0.5, cursor: canStart ? 'pointer' : 'default' }}>
            {busy && !asking ? 'Starting…' : 'Start'}
          </button>
        </div>
      )}

      {asking && (
        <ConfirmDialog testId="v2-reset-confirm" title="Start this chapter fresh?"
          text="Your old marks stay saved." yes="Yes, start fresh" no="No"
          busy={busy} onYes={doReset} onNo={() => setAsking(false)} />
      )}
    </div>
  );
}
